import { createCommerceLayerClient } from '@integration-layer/configs/commercelayer'
import { useOrderOnBehalf } from './useOrderOnBehalf'

/**
 * Composable for creating a Commerce Layer client with dynamic authentication.
 *
 * This function determines the authentication method based on the user's login status:
 * - For logged-in users, it uses customer-specific authentication
 * - For non-logged-in users, it uses default authentication
 *
 * @async
 * @returns {Promise<Object>} An object containing the Commerce Layer client.
 * @returns {Object} cl - The initialized Commerce Layer client.
 *
 * @example
 * // Usage in a component or another composable
 * const { cl } = await useCL()
 * // Now you can use the Commerce Layer client for various operations
 *
 * @throws {Error} If client creation fails due to authentication issues.
 */
export const useCL = async () => {
  const config = useRuntimeConfig()
  const { loggedIn, user } = useUserSession()
  const { onBehalfState, getToken: getOnBehalfToken } = useOrderOnBehalf()

  /**
   * Determines the appropriate authentication method, evaluated once at client
   * creation time. Callers must invoke useCL() after activating the OOB
   * checkout session (see applyCheckoutAccessToken) to get an oob_scoped client.
   */
  const auth = onBehalfState.value.isActive
    ? {
        organization: config.public.commercelayer.organization,
        getToken: getOnBehalfToken,
        refreshToken: getOnBehalfToken,
      }
    : import.meta.client && loggedIn.value
      ? useClCustomerAuth(user.value!.email)
      : useClAuth()

  /**
   * Creates a Commerce Layer client with the selected authentication.
   * @type {Object}
   */
  const cl = await createCommerceLayerClient(auth)

  return { cl }
}
